import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Loader2, Check, Globe } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STEPS = [
  "Fetching homepage & following redirects",
  "Detecting platform, plugins & schema",
  "Running technical & on-page checks",
  "Measuring Core Web Vitals",
  "Auditing AI-readiness & extractability",
  "Scoring & generating your fix list",
];

export default function NewAudit() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const stepTimer = useRef(null);
  const pollTimer = useRef(null);

  const cleanup = () => {
    clearInterval(stepTimer.current);
    clearInterval(pollTimer.current);
  };
  useEffect(() => cleanup, []);

  const start = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setError("");
    setRunning(true);
    setStep(0);
    stepTimer.current = setInterval(() => setStep((s) => (s < STEPS.length - 1 ? s + 1 : s)), 2600);

    try {
      const { data } = await api.post("/sites", { url });
      const siteId = data.site.id;
      pollTimer.current = setInterval(async () => {
        try {
          const r = await api.get(`/sites/${siteId}`);
          const st = r.data.site.status;
          if (st === "ready") {
            cleanup();
            setStep(STEPS.length);
            setTimeout(() => navigate(`/site/${siteId}`), 600);
          } else if (st === "error") {
            cleanup();
            setRunning(false);
            setError(r.data.site.error || "Audit failed — the site could not be reached.");
          }
        } catch (_) {}
      }, 2500);
    } catch (err) {
      cleanup();
      setRunning(false);
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Radar className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Enter a website and go</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Beacon crawls the site, audits it, and returns a Visibility Score with a prioritized fix list.
          </p>
        </div>

        {!running && (
          <form onSubmit={start} className="rounded-lg border border-border bg-card p-6">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="audit-url-input" className="pl-9 font-mono" placeholder="example.com" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus />
              </div>
              <Button data-testid="audit-start-button" type="submit">Run audit</Button>
            </div>
            {error && <p data-testid="audit-error" className="mt-3 text-sm text-destructive">{error}</p>}
            <p className="mt-3 text-xs text-muted-foreground">
              We respect robots.txt and identify as BeaconBot. First results in under a minute for typical sites.
            </p>
          </form>
        )}

        <AnimatePresence>
          {running && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-border bg-card p-6" data-testid="audit-progress">
              <p className="mb-5 font-mono text-sm text-muted-foreground">Auditing <span className="text-foreground">{url}</span></p>
              <div className="space-y-3">
                {STEPS.map((label, i) => {
                  const done = i < step;
                  const active = i === step;
                  return (
                    <motion.div key={i} initial={{ opacity: 0.4 }} animate={{ opacity: done || active ? 1 : 0.4 }} className="flex items-center gap-3 text-sm">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-border">
                        {done ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : active ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />}
                      </span>
                      <span className={done || active ? "text-foreground" : "text-muted-foreground"}>{label}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
